import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import '@/styles/globals.css';

import { RouterProvider } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { AppProviders } from '@/app/providers';
import { router } from '@/app/router';
import { initPlausible } from '@/lib/analytics/plausible';
import { initSentry } from '@/lib/sentry';
import '@/lib/zod-error-map'; // side-effect: registra el errorMap global de Zod en español

initSentry();
initPlausible();

// Kill-switch defense-in-depth: si por cualquier motivo el browser tiene
// un Service Worker registrado de una version previa que estaba cacheando
// bundles viejos, lo desregistramos y limpiamos sus caches en cuanto la
// app carga. El sw.js de /public ahora es un kill-script tambien (ver
// public/sw.js), pero esto cubre el caso de que el SW viejo haya
// interceptado el fetch del sw.js antes de llegar aqui.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => {
      for (const reg of regs) void reg.unregister();
    })
    .catch(() => {
      /* noop */
    });
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {
        /* noop */
      });
  }
}

// Cuando hacemos deploy a Cloudflare Pages, el index.html viejo en cache del
// browser sigue referenciando chunks con hashes que ya no existen. Apenas el
// router intenta `lazy()`-cargar uno (ej. gantt, list), el fetch falla con
// "Failed to load module script" o "Failed to fetch dynamically imported
// module". Esto mata navegaciones sin posibilidad de recovery más allá de un
// hard refresh manual del user.
//
// Solución: capturamos esos errores y forzamos `location.reload()` UNA sola
// vez por sesión (anti-loop con sessionStorage). El reload trae el index.html
// fresco con los hashes correctos y los chunks resuelven bien.
if (typeof window !== 'undefined') {
  const RELOAD_FLAG = 'meidei.chunk-error-reloaded';
  const isStaleChunkError = (msg: string | undefined): boolean => {
    if (!msg) return false;
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Failed to load module script') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Expected a JavaScript-or-Wasm module') ||
      msg.includes('error loading dynamically imported module')
    );
  };
  const reloadOnce = () => {
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === '1') return;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      /* sessionStorage puede fallar en modo incognito raro; reload igual */
    }
    window.location.reload();
  };
  // Si la app llega a montar y andar 5s sin volver a romper, asumimos que el
  // reload anterior solucionó el problema y limpiamos el flag.
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* noop */
    }
  }, 5000);

  window.addEventListener('error', (e) => {
    if (isStaleChunkError(e.message)) reloadOnce();
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const msg = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
    if (isStaleChunkError(msg)) reloadOnce();
  });
  // Vite emite este evento custom para fallos de <link rel="modulepreload">.
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    reloadOnce();
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </React.StrictMode>,
);
